import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Grid,
  GridItem,
  Icon,
  Flex,
  useColorModeValue,
  Collapse,
  IconButton,
  Tooltip,
  Button
} from '@chakra-ui/react'
import {
  addDays,
  addMonths,
  endOfMonth,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns'
import { FC, useMemo, useState } from 'react'
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiMinimize2
} from 'react-icons/fi'

import usePurchaseSchedule from '@hook/dashboard/use-purchase-schedule'

import type { IPurchaseScheduleData } from '@base/@types/providers/Dashboard/PurchaseScheduleProvider'

import {
  PurchaseScheduleStatus,
  STATUS_CONFIG,
  getRowStatus,
  parseDate,
  toDayKey
} from './utils'

interface CalendarDay {
  key: string
  date: Date
  dayNumber: number
  isCurrentMonth: boolean
  events: IPurchaseScheduleData[]
}

const LEGEND_ORDER: PurchaseScheduleStatus[] = [
  'noPrazo',
  'pendente',
  'emAtraso'
]

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
]

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const TOTAL_CELLS = 42 // 6 semanas * 7 dias

const CalendarView: FC = () => {
  const { data } = usePurchaseSchedule()
  const [isExpanded, setIsExpanded] = useState(true)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(new Date())
  )

  // Eventos agrupados por dia (local) da próxima compra prevista
  const eventsByDay = useMemo(() => {
    const map = new Map<string, IPurchaseScheduleData[]>()
    data.forEach(item => {
      const date = parseDate(item.proximaCompraPrevista)
      if (!date) return
      const key = toDayKey(date)
      const list = map.get(key)
      if (list) list.push(item)
      else map.set(key, [item])
    })
    return map
  }, [data])

  // Gerar os dias do calendário (6 semanas a partir do domingo)
  const calendarDays = useMemo(() => {
    const firstCell = startOfWeek(startOfMonth(visibleMonth))
    const days: CalendarDay[] = []
    for (let i = 0; i < TOTAL_CELLS; i++) {
      const date = addDays(firstCell, i)
      const key = toDayKey(date)
      days.push({
        key,
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: isSameMonth(date, visibleMonth),
        events: eventsByDay.get(key) ?? []
      })
    }
    return days
  }, [visibleMonth, eventsByDay])

  const eventsInMonth = useMemo(() => {
    const start = toDayKey(startOfMonth(visibleMonth))
    const end = toDayKey(endOfMonth(visibleMonth))
    let count = 0
    eventsByDay.forEach((list, key) => {
      if (key >= start && key <= end) count += list.length
    })
    return count
  }, [eventsByDay, visibleMonth])

  const getEventColor = (item: IPurchaseScheduleData) =>
    STATUS_CONFIG[getRowStatus(item)].color

  const today = new Date()

  const todayBgValue = useColorModeValue('blue.100', 'blue.700')
  const currentMonthColor = 'gray.800'
  const otherMonthColor = 'gray.400'

  const monthLabel = `${
    MONTHS[visibleMonth.getMonth()]
  } de ${visibleMonth.getFullYear()}`

  return (
    <Box
      bg="white"
      p={5}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      shadow="md"
    >
      <HStack
        mb={4}
        pb={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        justify="space-between"
      >
        <HStack>
          <Icon as={FiCalendar} color="blue.500" />
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            Calendário de Compras
          </Text>
        </HStack>
        <HStack spacing={2}>
          <IconButton
            aria-label="Mês anterior"
            icon={<Icon as={FiChevronLeft} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setVisibleMonth(m => subMonths(m, 1))}
          />
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="gray.700"
            minW="140px"
            textAlign="center"
          >
            {monthLabel}
          </Text>
          <IconButton
            aria-label="Próximo mês"
            icon={<Icon as={FiChevronRight} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setVisibleMonth(m => addMonths(m, 1))}
          />
          {!isSameMonth(visibleMonth, today) && (
            <Button
              size="xs"
              variant="outline"
              colorScheme="blue"
              onClick={() => setVisibleMonth(startOfMonth(new Date()))}
            >
              Hoje
            </Button>
          )}
          <IconButton
            aria-label={
              isExpanded ? 'Diminuir calendário' : 'Expandir calendário'
            }
            icon={<Icon as={isExpanded ? FiMinimize2 : FiMaximize2} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <IconButton
            aria-label={
              isCollapsed ? 'Mostrar calendário' : 'Ocultar calendário'
            }
            icon={<Icon as={isCollapsed ? FiChevronDown : FiChevronUp} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
        </HStack>
      </HStack>

      <Collapse in={!isCollapsed} animateOpacity>
        <VStack spacing={4} align="stretch">
          <Text fontSize="xs" color="gray.500">
            Exibe a data da <strong>próxima compra prevista</strong> de cada
            registro ({eventsInMonth.toLocaleString('pt-BR')} neste mês).
          </Text>

          {/* Nomes dos dias da semana */}
          <SimpleGrid columns={7} spacing={1}>
            {WEEKDAYS.map(day => (
              <Box
                key={day}
                p={isExpanded ? 2 : 1}
                textAlign="center"
                fontWeight="bold"
                color="gray.600"
                fontSize="sm"
              >
                {day}
              </Box>
            ))}
          </SimpleGrid>

          {/* Dias do calendário */}
          <Grid templateColumns="repeat(7, 1fr)" gap={1}>
            {calendarDays.map(day => {
              const maxVisible = isExpanded ? 3 : 1
              return (
                <GridItem
                  key={day.key}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={isExpanded ? 2 : 1}
                  bg={isSameDay(day.date, today) ? todayBgValue : 'white'}
                  minH={isExpanded ? '120px' : '60px'}
                  opacity={day.isCurrentMonth ? 1 : 0.7}
                >
                  <Flex justify="center" mb={1} minH="20px">
                    <Text
                      fontWeight={day.isCurrentMonth ? 'bold' : 'normal'}
                      color={
                        day.isCurrentMonth ? currentMonthColor : otherMonthColor
                      }
                      fontSize={isExpanded ? 'sm' : 'xs'}
                      lineHeight="1.2"
                    >
                      {day.dayNumber}
                    </Text>
                  </Flex>

                  <VStack
                    spacing={1}
                    align="start"
                    overflowY="auto"
                    maxH={isExpanded ? '80px' : '30px'}
                  >
                    {day.events.slice(0, maxVisible).map((event, idx) => {
                      const color = getEventColor(event)
                      const cfg = STATUS_CONFIG[getRowStatus(event)]
                      return (
                        <HStack
                          key={`${event.empresaId}-${event.lojaId}-${event.fornecedorId}-${event.categoriaCompactaId}-${idx}`}
                          bg={`${color}.100`}
                          p={isExpanded ? 1 : 0.5}
                          borderRadius="sm"
                          spacing={1}
                          w="100%"
                          border="1px solid"
                          borderColor={`${color}.200`}
                          title={`${cfg.label} - ${event.fornecedorNome}`}
                        >
                          <Icon
                            as={cfg.icon}
                            color={`${color}.500`}
                            w={isExpanded ? 3 : 2}
                            h={isExpanded ? 3 : 2}
                          />
                          {isExpanded && (
                            <VStack align="start" spacing={0}>
                              <Text
                                fontSize="xs"
                                fontWeight="medium"
                                color={`${color}.700`}
                                noOfLines={1}
                              >
                                {event.fornecedorNome}
                              </Text>
                              <Text
                                fontSize="xxs"
                                color={`${color}.600`}
                                noOfLines={1}
                              >
                                {event.categoriaCompactaNome}
                              </Text>
                            </VStack>
                          )}
                        </HStack>
                      )
                    })}

                    {day.events.length > maxVisible && (
                      <Tooltip
                        label={
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" fontSize="xs">
                              Eventos adicionais:
                            </Text>
                            {day.events.slice(maxVisible).map((event, idx) => (
                              <Text key={idx} fontSize="xs">
                                <strong>{event.fornecedorNome}</strong> -{' '}
                                {event.empresaNome}
                              </Text>
                            ))}
                          </VStack>
                        }
                        bg="gray.800"
                        color="white"
                        fontSize="xs"
                        borderRadius="md"
                        p={2}
                        placement="top"
                        hasArrow
                      >
                        <Text
                          fontSize={isExpanded ? 'xxs' : '2xs'}
                          color="blue.500"
                          cursor="pointer"
                          _hover={{
                            color: 'blue.600',
                            textDecoration: 'underline'
                          }}
                        >
                          +{day.events.length - maxVisible} mais
                        </Text>
                      </Tooltip>
                    )}
                  </VStack>
                </GridItem>
              )
            })}
          </Grid>

          {/* Legenda (também no modo compacto) */}
          <HStack spacing={4} justify="center" pt={2} flexWrap="wrap">
            {LEGEND_ORDER.map(status => {
              const cfg = STATUS_CONFIG[status]
              return (
                <HStack key={status} spacing={1}>
                  <Icon as={cfg.icon} color={`${cfg.color}.500`} boxSize={3} />
                  <Text fontSize="xs" color="gray.600">
                    {cfg.label}
                  </Text>
                </HStack>
              )
            })}
          </HStack>
        </VStack>
      </Collapse>
    </Box>
  )
}

export default CalendarView
